import type { components, paths } from '../../api/generated/schema'

export type MerchantType = components['schemas']['MerchantType']

/** Everything that narrows the list, plus where in it we are. */
export type MerchantFilters = {
  page: number
  pageSize: number
  search: string
  type: MerchantType | null
  includeInactive: boolean
}

/**
 * The default rows per page.
 *
 * The API's own default is 100 (`page_size` on `GET /api/v1/merchants`,
 * `minimum: 1, maximum: 500`). 50 rather than 100 because the server's default
 * is what an unspecified request happens to get, not a recommendation, and 50
 * rows is already more than any viewport shows at once.
 */
export const PAGE_SIZE = 50

/**
 * What the "Rows per page" control offers.
 *
 * Every value sits inside the API's documented `1..500`, and is sent verbatim
 * rather than clamped at the boundary — a value outside the bounds would be a
 * bug in this list, and silently correcting it at the edge is how a bug like
 * that survives. The server answers 422 for one.
 */
export const PAGE_SIZE_CHOICES = [25, 50, 100] as const

export const DEFAULT_FILTERS: MerchantFilters = {
  page: 1,
  pageSize: PAGE_SIZE,
  search: '',
  type: null,
  includeInactive: false,
}


/**
 * The label for each `MerchantType` — five values, both the type column
 * (`Merchants.tsx`) and the type filter (`MerchantsToolbar.tsx`) want the
 * same map, in the same order (`Object.entries` preserves string-key
 * insertion order). Kept here rather than in either route file: `Merchants`
 * importing from `MerchantsToolbar` or back would be a circular import
 * between two files that both need it.
 */
export const TYPE_LABEL: Record<MerchantType, string> = {
  business: 'Business',
  person: 'Person',
  contact: 'Contact',
  service: 'Service',
  other: 'Other',
}

/**
 * The query-param shape, DERIVED from the generated operation rather than
 * restated by hand.
 *
 * A hand-written named type here would let a backend rename of, say,
 * `include_inactive` silently stop reaching the wire: assigning an object
 * literal-shaped value to a separately-declared named type fires no
 * excess-property check, so the stale field would just be dropped and the
 * new one never sent — no error, no red test, wrong request. Pinned to
 * `paths[...]['get']['parameters']['query']` instead, a rename shows up as a
 * `tsc` error the next time `npm run api:build` regenerates the schema.
 */
export type MerchantQueryParams = NonNullable<
  paths['/api/v1/merchants']['get']['parameters']['query']
>

/** The query-param object, with absent filters genuinely absent. */
export function toQueryParams(f: MerchantFilters): MerchantQueryParams {
  const params: MerchantQueryParams = {
    page: f.page,
    page_size: f.pageSize,
    include_inactive: f.includeInactive,
  }
  const search = f.search.trim()
  if (search !== '') params.search = search
  if (f.type !== null) params.type = f.type
  return params
}
